import React, { useEffect, useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { COLORS } from '../utils/constants';
import { formatLength } from '../utils/length';
import { sectionLengthStatus } from '../utils/background';

interface SectionPropertiesModalProps {
  stateManager: StateManager;
  aId: string;
  bId: string;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '13px',
  color: '#636e72'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

/**
 * Properties dialog for a harness section (the run between two adjacent
 * vertices). Shows the wires carried by the section (hovering a row highlights
 * its route on the canvas), the editable section length and free-form notes such
 * as "shell" or "twisted pair".
 */
export const SectionPropertiesModal: React.FC<SectionPropertiesModalProps> = ({ stateManager, aId, bId, onClose }) => {
  const segment = stateManager.getSegment(aId, bId);
  const wires = stateManager.getWiresInSegment(aId, bId);

  const [lengthText, setLengthText] = useState(segment ? String(segment.length) : '0');
  const [notes, setNotes] = useState(segment?.notes ?? '');

  // Leave no highlight behind once the dialog closes.
  useEffect(() => () => stateManager.setHighlightedWire(undefined), [stateManager]);

  if (!segment) {
    return null;
  }

  const vertexName = (id: string): string => {
    const connector = stateManager.getState().connectors.find(c => c.id === id);
    return connector ? connector.name : 'Node';
  };

  // Distance implied by the vertex positions. Only offered as a suggestion once a
  // background is in place, since world units map to millimetres after calibration.
  const suggestedLength = stateManager.getState().background
    ? stateManager.suggestedSegmentLength(aId, bId)
    : null;
  // Status of the value currently in the field against the layout distance.
  const lengthStatus = suggestedLength !== null
    ? sectionLengthStatus(Math.round(Number(lengthText)), suggestedLength)
    : undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const length = Math.max(0, Math.round(Number(lengthText)));
    stateManager.beginBatch();
    if (Number.isFinite(length)) {
      stateManager.setSegmentLength(aId, bId, length);
    }
    stateManager.setSegmentNotes(aId, bId, notes.trim());
    stateManager.endBatch();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20000
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { onClose(); } }}
    >
      <div
        style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', width: '420px', maxHeight: '80vh', overflowY: 'auto' }}
        onKeyDown={(e) => { if (e.key === 'Escape') { onClose(); } }}
      >
        <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#2d3436' }}>Section Properties</h3>
        <div style={{ fontSize: '13px', color: '#636e72', marginBottom: '16px' }}>
          {vertexName(aId)} &rarr; {vertexName(bId)}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Length (mm)</label>
            <input type="number" min="0" step="1" value={lengthText} onChange={(e) => setLengthText(e.target.value)} autoFocus style={inputStyle} />
            {lengthStatus === 'auto' && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#636e72' }}>
                In sync with the layout
              </div>
            )}
            {lengthStatus === 'overwritten' && suggestedLength !== null && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: COLORS.PRIMARY, fontWeight: 600 }}>
                Overwritten &middot; layout calculates {formatLength(suggestedLength)}
              </div>
            )}
            {suggestedLength !== null && (
              <button
                type="button"
                onClick={() => setLengthText(String(suggestedLength))}
                title="Set the length from the distance between the two points on the layout"
                style={{
                  marginTop: '6px', padding: '4px 10px', border: `1px solid ${COLORS.PRIMARY}`,
                  borderRadius: '4px', backgroundColor: 'transparent', color: COLORS.HEADER,
                  cursor: 'pointer', fontSize: '12px'
                }}
              >
                Use layout length: {formatLength(suggestedLength)}
              </button>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. shell, twisted pair"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Wires in this section ({wires.length})</label>
            <div style={{ border: '1px solid #eee', borderRadius: '4px', overflow: 'hidden' }}>
              {wires.length === 0 && (
                <div style={{ padding: '10px 12px', fontSize: '13px', color: '#b2bec3' }}>No wires routed through this section.</div>
              )}
              {wires.map(wire => (
                <div
                  key={wire.id}
                  onMouseEnter={() => stateManager.setHighlightedWire(wire.id)}
                  onMouseLeave={() => stateManager.setHighlightedWire(undefined)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: '1px solid #f4f4f4', cursor: 'default' }}
                >
                  <span style={{
                    flex: '0 0 auto', width: '12px', height: '12px', borderRadius: '3px',
                    border: '1px solid #ccc', backgroundColor: wire.color ?? COLORS.WIRE_BUNDLE
                  }} />
                  <span style={{ flex: 1, fontSize: '14px', color: '#2d3436' }}>{wire.name}</span>
                  <span style={{ flex: '0 0 auto', fontSize: '12px', color: '#636e72' }}>{formatLength(stateManager.getWireLength(wire.id))}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white', color: '#636e72', cursor: 'pointer', fontSize: '14px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ padding: '8px 16px', border: 'none', borderRadius: '4px', backgroundColor: COLORS.ACTION, color: 'white', cursor: 'pointer', fontSize: '14px' }}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
